/* eslint-disable no-empty-pattern */
/* eslint no-param-reassign: 0 */
import moment from 'moment';
import { AccountManagerService } from '../services/account-manager.service';

const accountManagerState = {
  defaultCurrency: null,
  currencies: [],
  allCurrencies: [],
  countries: [],
  bankAccounts: null,
  swishBankAccounts: null,
  account: null,
  accountSettings: null,
  shoppingSettings: null,
  users: [],
  permissions: [],
  country: '',
};

const mutations = {
  setCountryCurrencies(state, data) {
    const currencies = [];
    const countries = [];
    // eslint-disable-next-line
    const { supported_bank_account_currencies: supported, default_currency: current, supported_payment_currencies, id } = data;
    // eslint-disable-next-line
    for (const currency of Object.keys(supported)) {
      currencies.push({ string: currency.toUpperCase(), value: currency });
      countries[currency] = supported[currency];
    }
    state.allCurrencies = supported_payment_currencies;
    state.currencies = currencies;
    state.countries = countries;
    state.defaultCurrency = current;
    state.country = id;
  },
  setBankAccounts(state, bankAccounts) {
    state.bankAccounts = bankAccounts;
  },
  setSwishBankAccounts(state, bankAccounts) {
    state.swishBankAccounts = bankAccounts;
  },
  setAccount(state, account) {
    state.account = account;
  },
  setAccountSettings(state, settings) {
    state.accountSettings = settings;
  },
  setShoppingSettings(state, settings) {
    state.shoppingSettings = settings;
  },
  unsetAccount(state) {
    state.account = null;
    state.bankAccounts = null;
    state.currencies = [];
    state.countries = [];
    state.defaultCurrency = null;
  },
  setUsers(state, users) {
    state.users = users;
  },
  setPermissions(state, permissions) {
    state.permissions = permissions;
  },
};

const getters = {
  account: state => state.account,
  accountSettings: state => state.accountSettings,
  activeSubscription: state => state.accountSettings && state.accountSettings.subscription && (state.accountSettings.subscription.active || state.accountSettings.subscription.trial),
  hasSubscriptionPlan: state => (state.accountSettings.subscription && state.accountSettings.subscription.trial ? true : state.accountSettings && state.accountSettings.stripe_plan && state.accountSettings.stripe_plan.subscription_id !== ''),
  trialEnded: (state) => {
    if (!state.accountSettings.subscription) {
      return false;
    }

    if (!state.accountSettings.subscription.active && !state.accountSettings.subscription.trial) {
      const trialEndDate = moment(state.accountSettings.stripe_plan.trial_ends_at);
      const subEndDate = moment(state.accountSettings.stripe_plan.subscription_canceled_at);
      if (parseInt(trialEndDate.format('YYYY'), 10) >= 2022 && trialEndDate.isAfter(subEndDate)) {
        return true;
      }
    }

    return false;
  },
  subscriptionEnded: (state) => {
    if (!state.accountSettings.subscription) {
      return false;
    }

    if (!state.accountSettings.subscription.active) {
      const subEndDate = moment(state.accountSettings.stripe_plan.subscription_canceled_at);
      if (parseInt(subEndDate.format('YYYY'), 10) >= 2022) {
        return true;
      }
    }

    return false;
  },
  shoppingSettings: state => state.shoppingSettings || {},
  currencies: state => state.currencies,
  allCurrencies: state => state.allCurrencies,
  countries: state => state.countries,
  defaultCurrency: state => state.defaultCurrency,
  bankAccounts: state => state.bankAccounts,
  swishBankAccounts: state => state.swishBankAccounts,
  users: state => state.users,
  permissions: state => state.permissions,
  payoutsDisabled: (state) => {
    if (!state.account) return null;
    return !state.account.verification.transfers_enabled;
  },
  chargesDisabled: (state) => {
    if (!state.account) return null;
    return !state.account.verification.charges_enabled;
  },
  accountStatus: (state) => {
    if (!state.account) return null;
    return state.account.verification.status;
  },
  // eslint-disable-next-line
  accountStatusState: (state, getters) => {
    if (!state.account) return ({ message: '', type: '' });
    const {
      accountStatus, payoutsDisabled, chargesDisabled, bankAccountNeeded, reqDue,
    } = getters;

    if (accountStatus === 'incomplete') {
      return {
        message: '',
        state: 'incomplete',
        type: 'fatal',
      };
    }

    if (accountStatus === 'pending') {
      return {
        message: 'account.pending',
        state: 'pending',
        type: '',
      };
    }

    if ((accountStatus === 'unverified' || accountStatus === '') && reqDue) {
      return {
        message: chargesDisabled && (payoutsDisabled && !bankAccountNeeded) ? 'account.update_activate' : 'account.update_prevent',
        state: 'unverified',
        type: chargesDisabled && (payoutsDisabled && !bankAccountNeeded) ? 'fatal' : 'warning',
      };
    }

    if (accountStatus === 'verified' && reqDue) {
      return {
        message: 'account.update_prevent',
        state: 'verified',
        type: 'warning',
      };
    }

    return {
      message: 'account.verified',
      state: 'verified',
      type: 'success',
    };
  },
  bankAccountNeeded: (state) => {
    if (!state.account) return false;
    const { verification: { verification_details } } = state.account;
    if (!verification_details) return false;
    if (typeof verification_details.fields_needed !== 'undefined') { return false; }
    const { currently_due, past_due } = verification_details;
    const due = currently_due.concat(past_due);
    return !state.account.verification.transfers_enabled && due.includes('external_account');
  },
  reqDeadline: (state) => {
    if (!state.account) {
      return null;
    }

    if (state.account?.verification?.verification_details?.current_deadline === 0) {
      return null;
    }

    return state.account?.verification?.verification_details?.current_deadline;
  },
  reqDue: (state) => {
    if (!state.account) return false;
    const { verification: { verification_details } } = state.account;
    if (!verification_details) return false;
    if (typeof verification_details.fields_needed !== 'undefined') {
      return true;
    }

    const { currently_due, eventually_due } = verification_details;

    const fields = [...new Set(currently_due.concat(eventually_due))];
    const needed = fields.reduce((arr, field) => {
      if (field !== 'external_account') {
        arr.push(field);
      }
      return arr;
    }, []);

    if (needed.length > 0) {
      return true;
    }

    return false;
  },
  /**
   * Returns a boolean wether or not PPV should be enabled
   *
   * @param {Object} state
   * @return {Boolean}
   */
  ppvEnabled: (state) => {
    if (!state.account || !state.account.verification) return false;
    const { account: { company: { address: { country = '' } = {} } = {}, verification: { charges_enabled = false } = {} } = {} } = state;
    if (country.toLowerCase() === 'se') {
      return true;
    }
    return charges_enabled;
  },
  /**
   * Return the country that user has chosen on its account
   * @return {String}
   */
  accountCountry: state => (state.account ? state?.account?.company?.address?.country : ''),
  /**
   * Return an array of features this account has enabled
   * @param {Object} state
   * @returns {Array}
   */
  featuresEnabled: (state) => {
    const features = [];
    if (state.accountSettings) {
      const {
        ppv: {
          enabled: ppvEnabled = false,
        } = {},
        shopping: {
          enabled: shoppingEnabled = false,
        } = {},
        multi_camera: {
          enabled: multiCameraEnabled = false,
        } = {},
        radio: {
          enabled: radioEnabled = false,
        } = {},
        scorebar: {
          enabled: scorebarEnabled = false,
        } = {},
      } = state.accountSettings;

      if (ppvEnabled) {
        features.push('ppv');
      }

      if (shoppingEnabled) {
        features.push('lvs');
      }

      if (multiCameraEnabled) {
        features.push('multi_camera');
      }

      if (radioEnabled) {
        features.push('radio');
      }

      if (scorebarEnabled) {
        features.push('scorebar');
      }
    }

    return features;
  },
};

const actions = {
  checkAccountName: async ({}, name) => {
    try {
      const data = await AccountManagerService.checkAccountName(name);
      return data;
    } catch (e) {
      return false;
    }
  },
  /**
   * Get permissions you can update/set on new users
   * @param {Object} obj
   * @param {Function} obj.commit
   * @return {Boolean}
   */
  getPermissions: async ({ commit }) => {
    try {
      const permissions = await AccountManagerService.getPermissions();
      commit('setPermissions', permissions);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Get users that are linked to this account
   * @param {Object} obj
   * @param {Function} obj.commit
   * @return {Boolean}
   */
  getUsers: async ({ commit }) => {
    try {
      const users = await AccountManagerService.getUsers();
      commit('setUsers', users);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Create user
   * @param {Object} obj - empty
   * @return {Boolean}
   */
  addUser: async ({}, user) => {
    try {
      const data = await AccountManagerService.addUser(user);
      return data;
    } catch (e) {
      return {
        error: true,
        message: e.error.message || '',
      };
    }
  },
  /**
   * Remove user
   * @param {Object} obj - empty
   * @param {String} user
   * @return {Boolean}
   */
  removeUser: async ({}, user) => {
    try {
      await AccountManagerService.removeUser(user);
      return true;
    } catch (e) {
      return false;
    }
  },
  getCountryCurrencies: async ({ commit, state }) => {
    try {
      const cc = await AccountManagerService.getCountryCurrencies();
      commit('setCountryCurrencies', cc);
      return true;
    } catch (e) {
      const { account: { company: { address: { country } } } } = state;
      if (country.toLowerCase() === 'se') {
        state.allCurrencies = ['sek'];
        state.currencies = [{ string: 'SEK', value: 'sek' }];
        // eslint-disable-next-line
        state.countries['sek'] = ['SE'];
        state.defaultCurrency = 'sek';
        state.country = 'SE';
        return true;
      }
      return false;
    }
  },
  getBankAccounts: async ({ commit }) => {
    try {
      const bankAccounts = await AccountManagerService.getBankAccounts();
      commit('setBankAccounts', bankAccounts);
      return true;
    } catch (e) {
      commit('setBankAccounts', []);
      return false;
    }
  },
  getSwishBankAccounts: async ({ commit }) => {
    try {
      const bankAccounts = await AccountManagerService.getSwishBankAccounts();
      commit('setSwishBankAccounts', bankAccounts);
      return true;
    } catch (e) {
      commit('setSwishBankAccounts', []);
      return false;
    }
  },
  setDefaultBankAccount: async ({}, { userId: account_id, bank_account_id }) => {
    try {
      await AccountManagerService.setDefaultBankAccount({ account_id, bank_account_id });
      return true;
    } catch (e) {
      return false;
    }
  },
  addBankAccount: async ({}, { userId: account_id, token }) => {
    try {
      await AccountManagerService.addBankAccount({ account_id, token });
      return true;
    } catch (e) {
      return false;
    }
  },
  addSwishBankAccount: async ({}, { account_type, account_number }) => {
    if (!account_type || !account_number) return false;
    try {
      await AccountManagerService.addSwishBankAccount({ account_type, account_number });
      return true;
    } catch (e) {
      return false;
    }
  },
  removeBankAccount: async ({}, { id }) => {
    try {
      await AccountManagerService.removeBankAccount(id);
      return true;
    } catch (e) {
      return false;
    }
  },
  removeSwishBankAccount: async ({}, id) => {
    try {
      await AccountManagerService.removeSwishBankAccount(id);
      return true;
    } catch (e) {
      return false;
    }
  },
  getAccount: async ({ commit, rootGetters }) => {
    try {
      const assumed = rootGetters['User/assumed'] || rootGetters['User/assumeParam'];
      const user = rootGetters['User/user'];
      const account = await AccountManagerService.getAccount(assumed || user.account_id);
      commit('setAccount', account);
      return true;
    } catch (e) {
      return false;
    }
  },

  updateAccount: async ({ dispatch, rootGetters }, payload) => {
    try {
      const assumed = rootGetters['User/assumed'];
      const user = rootGetters['User/user'];
      await AccountManagerService.updateAccount(assumed || user.account_id, payload);
      await dispatch('getAccount');
      return true;
    } catch (e) {
      return false;
    }
  },

  getAccountSettings: async ({ commit }, id) => {
    try {
      const settings = await AccountManagerService.getAccountSettings(id);
      commit('setAccountSettings', settings);
      return true;
    } catch (e) {
      commit('setAccountSettings', {});
      return false;
    }
  },

  getAccountOnboarding: async ({}) => {
    try {
      // eslint-disable-next-line
      const settings = await AccountManagerService.getAccountOnboarding();
      return settings;
    } catch (e) {
      return false;
    }
  },

  getShoppingSettings: async ({ commit }) => {
    try {
      const settings = await AccountManagerService.shoppingSettings();
      commit('setShoppingSettings', settings);
      return true;
    } catch (e) {
      return false;
    }
  },
  disconnectProvider: async ({}) => {
    try {
      await AccountManagerService.deleteShoppingSettings();
      return true;
    } catch (e) {
      return false;
    }
  },
  unassumeAccount: async ({ commit, dispatch, rootGetters }) => {
    try {
      if (window.assumeParam) {
        delete window.assumeParam;
        commit('User/setAssumeParam', false, { root: true });
      } else {
        await AccountManagerService.unassumeAccount();
      }
      const user = rootGetters['User/user'];
      commit('User/setAssumed', false, { root: true });
      await dispatch('getAccount', user.account_id);
      await dispatch('getAccountSettings', user.account_id);
      await dispatch('getShoppingSettings');
      await dispatch('getAccountSettings');
      return true;
    } catch (e) {
      return false;
    }
  },
  assumeAccount: async ({ commit, dispatch }, { account, permission }) => {
    try {
      await AccountManagerService.assumeAccount(parseInt(account, 10), permission);
      commit('User/setAssumed', account, { root: true });
      await dispatch('getAccount', account);
      await dispatch('getAccountSettings', account);
      await dispatch('getShoppingSettings');
      await dispatch('getAccountSettings');
      return true;
    } catch (e) {
      return false;
    }
  },
  setLegalCountry: async ({ dispatch, rootGetters }, country) => {
    try {
      await AccountManagerService.setLegalCountry(country);
      const user = rootGetters['User/user'];
      await dispatch('getAccount', user.account_id);
      return true;
    } catch (e) {
      return false;
    }
  },
  updateAccountInformation: async ({ dispatch }, { country, activities }) => {
    try {
      await AccountManagerService.updateAccountInformation({ country, activities });
      await dispatch('getAccountSettings');
      return true;
    } catch (e) {
      return false;
    }
  },
  getChildAccounts: async ({ rootGetters }, params = {}) => {
    try {
      const assumed = rootGetters['User/assumed'];

      let payload = {
        page: 1,
        per_page: 20,
      };

      payload = { ...payload, ...params };

      // eslint-disable-next-line
      if (assumed !== null && !isNaN(assumed)) {
        payload.account_id = assumed;
      }

      const data = await AccountManagerService.getChildAccounts(payload);

      return data;
    } catch (e) {
      return false;
    }
  },
  createChildAccount: async ({}, payload) => {
    try {
      await AccountManagerService.createChildAccount(payload);
      return { success: true };
    } catch (e) {
      return { error: typeof e.error !== 'undefined' ? e.error : e };
    }
  },
  verifyInvite: async ({}, uuid) => {
    try {
      const invitedata = await AccountManagerService.verifyInvite(uuid);
      return invitedata;
    } catch (e) {
      return false;
    }
  },
  declineInvite: async ({}, uuid) => {
    try {
      await AccountManagerService.declineInvite(uuid);
      return true;
    } catch (e) {
      return false;
    }
  },
  onboardingVerification: async () => {
    try {
      const links = await AccountManagerService.onboardingVerification();
      return links;
    } catch (e) {
      return false;
    }
  },
  onboardingUpdate: async () => {
    try {
      const links = await AccountManagerService.onboardingUpdate();
      return links;
    } catch (e) {
      return false;
    }
  },
  getConnectLink: async () => {
    try {
      const links = await AccountManagerService.getConnectLink();
      return links;
    } catch (e) {
      return false;
    }
  },
  connectStripeAccount: async ({}, code) => {
    try {
      await AccountManagerService.connectStripeAccount(code);
      return true;
    } catch (e) {
      return false;
    }
  },
  updateSettings: async ({}, payload) => {
    try {
      await AccountManagerService.updateSettings(payload);
      return true;
    } catch (e) {
      return false;
    }
  },
};

export default {
  namespaced: true,
  state: accountManagerState,
  mutations,
  actions,
  getters,
};
